import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import AuthCard from './AuthCard';
import styles from './auth.module.css';

const MIN_PASSWORD_LENGTH = 8;

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { t } = useTranslation();
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    const result = await register(username, email, password);

    if (result.success) {
      setRegistered(true);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (registered) {
    return (
      <Container component="main" maxWidth="xs">
        <Box className={styles.wrap}>
          <AuthCard>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {t('auth.checkYourEmail')}
            </Typography>
            <Alert severity="success" className={styles.alert}>
              <Trans i18nKey="auth.verificationSent" values={{ email }} components={{ strong: <strong /> }} />
            </Alert>
            <Box className={`${styles.linkRowCenter} ${styles.mt3}`}>
              <Link to="/login" className={styles.link}>
                <Typography variant="body2" color="primary">
                  {t('auth.backToSignIn')}
                </Typography>
              </Link>
            </Box>
          </AuthCard>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box className={styles.wrap}>
        <AuthCard>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t('auth.signUp')}
          </Typography>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className={styles.formBox}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t('auth.username')}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={password.length > 0 && password.length < MIN_PASSWORD_LENGTH}
              helperText={password.length > 0 && password.length < MIN_PASSWORD_LENGTH ? t('auth.passwordTooShort', { min: MIN_PASSWORD_LENGTH }) : ''}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? t('auth.passwordsDoNotMatch') : ''}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={styles.submitBtnLg}
              disabled={loading}
            >
              {loading ? t('auth.signingUp') : t('auth.signUp')}
            </Button>
            <Box className={`${styles.linkRowCenter} ${styles.mt2}`}>
              <Link to="/login" className={styles.link}>
                <Typography variant="body2" color="primary">
                  {t('auth.haveAccount')}
                </Typography>
              </Link>
            </Box>
          </Box>
        </AuthCard>
      </Box>
    </Container>
  );
};

export default Register;
